import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function QualityPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Research Peptide Quality & Testing | 99%+ Purity with COA',
    description: 'DN Biotech quality standards: 99%+ purity guarantee, third-party HPLC testing, pharmaceutical-grade manufacturing, COA documentation, and cold-chain shipping for research peptides.',
    keywords: 'research peptide quality, HPLC testing, COA certificate, peptide purity, pharmaceutical grade peptides, third-party testing',
    canonical: `${siteUrl}/quality`,
    schema: generateWebPageSchema(siteUrl, '/quality', 'Research Peptide Quality & Testing | 99%+ Purity with COA', 'DN Biotech quality standards: 99%+ purity guarantee, third-party HPLC testing, pharmaceutical-grade manufacturing, COA documentation, and cold-chain shipping for research peptides.')
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Quality & Testing
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Pharmaceutical-grade purity backed by rigorous third-party testing
          </p>
        </div>
      </section>

      {/* Testing Process */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-6">Our Testing Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every batch undergoes comprehensive analysis to ensure it meets our strict quality standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: 'ri-flask-line',
                title: 'Raw Material Verification',
                description: 'All raw materials are sourced from certified suppliers and verified for authenticity and purity before processing.'
              },
              {
                step: '02',
                icon: 'ri-microscope-line',
                title: 'HPLC Analysis',
                description: 'High-Performance Liquid Chromatography testing confirms molecular structure, purity percentage, and identifies any impurities.'
              },
              {
                step: '03',
                icon: 'ri-file-list-3-line',
                title: 'COA Documentation',
                description: 'Complete Certificate of Analysis provided with detailed test results, methodology, and batch information.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md border border-gray-200">
                <div className="text-5xl font-bold text-primary-100 mb-4">{item.step}</div>
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-full mb-6">
                  <i className={`${item.icon} text-3xl text-primary-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-navy-900 mb-12 text-center">Quality Standards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: 'ri-award-line',
                title: '99%+ Purity Guarantee',
                description: 'All peptides meet or exceed 99% purity as verified by HPLC analysis. We reject any batch that does not meet this standard.'
              },
              {
                icon: 'ri-test-tube-line',
                title: 'Third-Party Testing',
                description: 'Independent laboratory verification ensures unbiased results and complete transparency in our quality claims.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Pharmaceutical Grade',
                description: 'Manufacturing processes follow pharmaceutical industry standards for sterility, handling, and quality control.'
              },
              {
                icon: 'ri-file-text-line',
                title: 'Batch Traceability',
                description: 'Every product includes batch number and manufacturing date for complete traceability and quality assurance.'
              },
              {
                icon: 'ri-temp-cold-line',
                title: 'Proper Storage',
                description: 'All compounds stored at optimal temperatures in controlled environments to maintain stability and potency.'
              },
              {
                icon: 'ri-truck-line',
                title: 'Cold-Chain Shipping',
                description: 'Temperature-controlled logistics with insulated packaging and ice packs to preserve compound integrity during transit.'
              }
            ].map((item, index) => (
              <div key={index} className="flex space-x-4 bg-white rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COA Information */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-primary-500 rounded-2xl mx-auto mb-6">
                <i className="ri-file-list-3-line text-4xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-navy-900 mb-4">
                Certificate of Analysis (COA)
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Every product comes with a comprehensive Certificate of Analysis from an independent third-party laboratory
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-xl font-bold text-navy-900 mb-4">What's Included in Our COA:</h3>
              <ul className="space-y-3">
                {[
                  'HPLC purity analysis results',
                  'Mass spectrometry data',
                  'Molecular weight confirmation',
                  'Testing methodology details',
                  'Batch number and manufacturing date',
                  'Storage and handling recommendations',
                  'Laboratory certification information'
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-primary-600 mr-3 mt-1"></i>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}